import { useAuth } from '@context/AuthContext';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useMultisucursalGate } from '@utils/hooks/useMultisucursalGate';
import { useEffect, useRef } from 'react';

/**
 * Multisucursal — Auto-sanado silencioso de sesiones inválidas.
 *
 * Contexto: históricamente el panel permitía cambiar de sucursal usando un token emitido
 * para otra sucursal sin validar (a) que el usuario fuera root ni (b) que el comercio
 * tuviera el módulo PEDIDOS_MULTISUCURSAL. Quedan sesiones "atrapadas" en una sucursal que
 * no corresponde al usuario.
 *
 * Como el enforcement elegido es L1 (gate de emisión en backend) + FE — sin rechazo
 * por-request en el autorizador — el FE detecta la sesión inválida con los datos
 * autoritativos de `account-details`:
 *   - `homeCommerce` (sucursal propia = user.id_comercio, token firmado por el backend)
 *   - `useMultisucursalGate()` (root, o no-root con permiso) sobre el comercio ACTIVO
 *
 * Regla: si el comercio activo NO es el propio Y el usuario no es elegible (gate multisucursal),
 * se restaura silenciosamente la sucursal propia. Sin toast (decisión de producto).
 */
export const useBranchSessionGuard = () => {
  const { refreshLogin } = useAuth();
  const { homeCommerce } = useSubscription();
  const healingRef = useRef(false);

  const eligible = useMultisucursalGate();

  useEffect(() => {
    if (healingRef.current) return;
    if (!homeCommerce?.id_comercio || !homeCommerce.token) return;

    const currentId = localStorage.getItem('id_commerce');
    if (!currentId) return;

    const inForeignBranch = currentId !== homeCommerce.id_comercio;

    if (!inForeignBranch) {
      // Ya estamos en la sucursal propia: limpiar el guard para permitir futuros sanados.
      healingRef.current = false;
      return;
    }

    // En una sucursal ajena: solo es válido si el usuario es root y el comercio activo
    // tiene el módulo. En cualquier otro caso, restaurar la sucursal propia.
    if (!eligible) {
      healingRef.current = true;
      refreshLogin(homeCommerce.token, homeCommerce.id_comercio, {
        nombre: homeCommerce.nombre,
        logotipo: homeCommerce.logotipo,
        id_path: homeCommerce.id_path,
      });
    }
  }, [homeCommerce, eligible, refreshLogin]);
};

export default useBranchSessionGuard;
